<!-- beaut-545. The specialist, shown BEFORE a procedure is chosen.
     It sits above the @if so it survives both branches: while the service
     picker fills the panel, and after, when the form replaces it. Previously
     the panel opened straight into the picker with no header at all, so the
     customer's own specialist was invisible until a procedure was selected.

     `employee()` follows the picker in the form below — swapping is allowed
     and the header must not lie about who the booking is now for. The
     fallback covers only the first paint: `data.employee` is handed in by the
     opener, so the name is right immediately instead of appearing once
     `employeeResource` resolves. It is scoped to the ORIGINAL employee id, or
     a swap would briefly flash the previous person's name while the new one
     loads. -->
@if (headerEmployee(); as specialist) {
  <div class="booking-header" data-test="booking-employee">
    <span class="booking-header__label">
      {{ 'calendar.form.employee' | translate }}
    </span>
    <span class="booking-header__name">
      {{ specialist?.user?.fullName }}
    </span>
    @if (specialist?.position?.name) {
      <span class="booking-header__position">
        {{ specialist?.position?.name }}
      </span>
    }
  </div>
}

@if (needChooseService()) {
  <beautyapp-choose-service
    [userId]="data.userId"
    [employeeId]="employeeId()"
    [roomId]="selectedRoomId()"
    [bookingDate]="form.controls.bookingDate.value"
    (serviceSelected)="chooseService($event)"
    (offerServiceSelected)="chooseOfferService($event)"
    (packageServiceSelected)="choosePackageService($event)"
  ></beautyapp-choose-service>
} @else {
  <form [formGroup]="form" class="flex flex-col gap-5">
    <!-- ══ Zone: Procedure & pricing ══ -->
    <section class="flex flex-col gap-3">
      <div class="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-600">
        <span>{{ 'calendar.form.procedureAndPrice' | translate }}</span>
        <span class="flex-1 h-px bg-gray-200"></span>
      </div>
      <div class="flex gap-4 items-start flex-wrap">
      <div class="flex flex-col gap-1">
        <div class="text-xs flex items-center h-[24px]">
          {{ 'calendar.form.procedure' | translate }}
        </div>
        <div
          class="flex gap-2 justify-between items-center border border-primary-400 rounded-[7px] px-4 py-2 w-96 min-h-[40px]">
          <div class="flex items-center gap-2">
            {{ service()?.name }}
            @if (service()?.offerPurchaseId) {
              <nz-tag nzColor="gold" class="text-xs">
                <i nz-icon nzType="gift" nzTheme="outline"></i>
                {{ 'calendar.offerService' | translate }}
              </nz-tag>
            }
            @if (service()?.packagePurchaseId) {
              <nz-tag nzColor="cyan" class="text-xs">
                <i nz-icon nzType="gift" nzTheme="outline"></i>
                {{ 'calendar.packageService' | translate }}
              </nz-tag>
            }
          </div>
          <div>
            <button
              nz-button
              nzType="primary"
              nzSize="small"
              (click)="needChooseService.set(true)"
              >
              <i nz-icon nzType="edit" nzTheme="outline"></i>
            </button>
          </div>
        </div>
      </div>


      <div class="flex flex-col gap-3 flex-initial w-[100px] duration-field">
        <beautyapp-input
          label="{{'calendar.form.duration' | translate}}"
          placeholder="{{'calendar.form.time' | translate}}"
          formControlName="time"
          mask="00:00"
          required
          size="large"
        ></beautyapp-input>
      </div>
      @if (service()?.serviceType === 'STANDARD') {
        <div class="flex flex-col gap-3 flex-1">
          <beautyapp-input
            label="{{'calendar.form.price' | translate}}"
            placeholder="{{'calendar.form.gel' | translate}}"
            type="number"
            prefix="₾"
            formControlName="price"
            required
            size="large"
          ></beautyapp-input>
        </div>
        <div class="flex flex-col gap-3 flex-1">
          <beautyapp-input
            label="{{'calendar.form.discount' | translate}}"
            placeholder="{{'calendar.form.percent' | translate}}"
            prefixIcon="percentage"
            type="number"
            formControlName="salePercent"
            size="large"
            [min]="0"
            [max]="100"
            (ngModelChange)="changePercentage($event)"
          ></beautyapp-input>
        </div>
        <div class="flex flex-col gap-3 flex-1">
          <beautyapp-input
            label="{{'calendar.form.discountedPrice' | translate}}"
            placeholder="{{'calendar.form.gel' | translate}}"
            prefix="₾"
            type="number"
            formControlName="salePrice"
            size="large"
          ></beautyapp-input>
        </div>
      } @else if (service()?.serviceType === 'MINUTE') {

        <div class="flex flex-col gap-1 h-[40px] self-end">
          <div class="flex gap-1 items-center h-[20px] ">
            <div class="flex items-center">
              წუთის ფასი:
            </div>
            @if (+service()?.discountPrice > 0) {
              <div class="flex items-center gap-1">
                <div>
                  {{ service()?.discountPrice }} <i class="lari lari-c"></i>
                </div>
                <del>{{ service()?.price }} <i class="lari lari-c"></i></del>
              </div>
            }
            @if (+service()?.discountPrice <= 0) {
              <div>
                {{ service()?.price }} <i class="lari lari-c"></i>
              </div>
            }
          </div>

          <div class="flex gap-1 items-center h-[20px]">
            <div class="flex items-center">
              ფასი:
            </div>
            @if (+(form.get('salePrice')?.value || 0) > 0) {
              <div class="flex items-center gap-1">
                <div>
                  {{ form.get('salePrice')?.value }} <i class="lari lari-c"></i>
                </div>
                <del>{{ form.get('price')?.value }} <i class="lari lari-c"></i></del>
              </div>
            }
            @if (+(form.get('salePrice')?.value || 0) <= 0) {
              <div>
                {{ form.get('price')?.value }} <i class="lari lari-c"></i>
              </div>
            }
          </div>
        </div>
      }
      </div>
    </section>
    <!-- ══ Zone: Time & room ══ -->
    <section class="flex flex-col gap-2">
      <div class="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-600">
        <span>{{ 'calendar.form.time' | translate }} / {{ 'calendar.form.room' | translate }}</span>
        <span class="flex-1 h-px bg-gray-200"></span>
      </div>
      <div class="flex items-start gap-3 flex-wrap">
        <div class="flex-initial">
          <beautyapp-time-picker
            label="{{ 'calendar.form.startingTime' | translate }}"
            placeholder="{{ 'calendar.form.startingTime' | translate }}"
            formControlName="startTime"
            required
            size="large"
          ></beautyapp-time-picker>
        </div>
        <div class="flex-initial">
          <beautyapp-time-picker
            label="{{ 'calendar.form.finishingTime' | translate }}"
            placeholder="{{ 'calendar.form.finishingTime' | translate }}"
            formControlName="endTime"
            required
            size="large"
          ></beautyapp-time-picker>
        </div>
        <div class="flex-1 min-w-[240px]">
          <beautyapp-select
            formControlName="roomId"
            [data]="availableRooms()"
            label="{{ 'calendar.form.room' | translate }}"
            placeholder="{{ 'calendar.form.selectRoom' | translate }}"
            valueMember="id"
            displayMember="name"
            size="large"
          ></beautyapp-select>
        </div>
      </div>
      @if (form.getError('dateRange')) {
        <div class="text-red-500 text-sm">
          {{ 'calendar.form.errors.dateRangeError' | translate }}
        </div>
      }
    </section>

    <!-- ══ Zone: Staff (hidden entirely for Employee role) ══ -->
    <ng-container *ngxPermissionsExcept="['Employee']">
      <section class="flex flex-col gap-2">
        <div class="flex items-center gap-3">
          <div class="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-600 flex-1">
            <span>{{ 'calendar.form.employees' | translate }}</span>
            <span class="flex-1 h-px bg-gray-200"></span>
          </div>
          @if (selectedRoomAllowsMultipleEmployees()) {
            <button
              nz-button
              nzType="primary"
              nzSize="small"
              type="button"
              (click)="addAdditionalEmployee()"
            >
              <i nz-icon nzType="plus" nzTheme="outline"></i>
              {{ 'calendar.form.addEmployee' | translate }}
            </button>
          }
        </div>

        <!-- Column header (once) -->
        <div class="flex gap-2 text-xs text-gray-600 px-1">
          <div class="flex-1">{{ 'calendar.form.employee' | translate }}</div>
          <ng-container *ngxPermissionsOnly="['field.employee_salary']">
            <div class="flex-1">{{ 'calendar.form.fixatedSalary' | translate }}</div>
            <div class="flex-1">{{ 'calendar.form.salary%' | translate }}</div>
          </ng-container>
          <div class="w-[52px]"></div>
        </div>

        <!-- Main employee -->
        <div class="flex gap-2 items-start">
          <div class="flex-1">
            <!-- beaut-545. When the visit already fixed the employee this is
                 a plain read-out, not a greyed control.

                 `[disabled]` was the obvious move and does NOT work here: the
                 shared select only flips a flag on its value accessor, and
                 Angular's own setUpControl calls setDisabledState(false) when
                 formControlName registers, right after the input is set. The
                 flag is overwritten before the first paint and the dropdown
                 opens as usual — walked in the browser, not deduced.

                 control.disable() would hold, but a disabled control drops out
                 of form.value, and that value IS the booking's employee. So the
                 control stays enabled and simply is not rendered. -->
            @if (employeeLocked) {
              <!-- headerEmployee(), not employee(): it carries the first-paint
                   fallback from nzData, so the name is there immediately
                   instead of a blank box while the lookup resolves. -->
              <div class="locked-employee" data-test="booking-employee-locked">
                {{ headerEmployee()?.user?.fullName }}
              </div>
            } @else {
              <beautyapp-select
                formControlName="employeeId"
                data-test="booking-employee-select"
                [data]="visitAddFacade.employee$ | async"
                placeholder="{{ 'calendar.form.employee' | translate }}"
                valueMember="id"
                displayMember="fullName"
                required
                size="large"
              ></beautyapp-select>
            }
          </div>
          <ng-container *ngxPermissionsOnly="['field.employee_salary']">
            <div class="flex-1">
              <beautyapp-input
                placeholder="{{'calendar.form.fixatedSalary' | translate}}"
                type="number"
                prefix="₾"
                formControlName="salaryFixed"
                [readonly]="isEmployee"
                size="large"
              ></beautyapp-input>
            </div>
            <div class="flex-1">
              <beautyapp-input
                placeholder="{{'calendar.form.salary%' | translate}}"
                prefixIcon="percentage"
                type="number"
                formControlName="salaryPercent"
                [min]="0"
                [max]="100"
                [errors]="{ min: 'validators.percentRange', max: 'validators.percentRange' }"
                [readonly]="isEmployee"
                size="large"
              ></beautyapp-input>
              @if (isEnableSalaryPercentFromPrice) {
                <label nz-checkbox class="mt-1"
                formControlName="salaryPercentFromPrice">{{ 'calendar.form.fromValue' | translate }}</label>
              }
            </div>
          </ng-container>
          <div class="w-[52px]"></div>
        </div>

        <ng-container>
          @if (selectedRoomAllowsMultipleEmployees()) {
            <nz-card class="mt-3">
              <div class="flex flex-col gap-2" formArrayName="additionalEmployees">
                <div class="flex justify-between items-center">
                  <div class="font-semibold">{{ 'calendar.form.additionalEmployees' | translate }}</div>
                  <button
                    nz-button
                    nzType="primary"
                    nzSize="small"
                    (click)="addAdditionalEmployee()"
                    type="button"
                    >
                    <i nz-icon nzType="plus" nzTheme="outline"></i>
                    {{ 'calendar.form.addEmployee' | translate }}
                  </button>
                </div>

                <!-- Column labels shown once (were repeated on every row) -->
                @if (additionalEmployeesFrom.controls.length) {
                  <div class="flex gap-2 text-xs text-gray-600 px-1">
                    <div class="flex-1">{{ 'calendar.form.employee' | translate }}</div>
                    <ng-container *ngxPermissionsOnly="['field.employee_salary']">
                      <div class="flex-1">{{ 'calendar.form.fixatedSalary' | translate }}</div>
                      <div class="flex-1">{{ 'calendar.form.salary%' | translate }}</div>
                    </ng-container>
                    <div class="w-[52px]"></div>
                  </div>
                }

                @for (employee of additionalEmployeesFrom.controls; track $index; let idx = $index) {
                  <div class="flex gap-2 items-start" formGroupName="{{ idx }}">
                    <div class="flex-1">
                      <nz-select
                        [nzPlaceHolder]="'calendar.form.selectEmployee' | translate"
                        [nzSize]="'large'"
                        formControlName="employeeId"
                        (ngModelChange)="onAdditionalEmployeeChange(idx, $event)"
                        class="w-full"
                        >
                        @for (emp of (visitAddFacade.employee$ | async); track emp.id) {
                          <nz-option [nzValue]="emp.id" [nzLabel]="emp.fullName"></nz-option>
                        }
                      </nz-select>
                    </div>

                    <ng-container *ngxPermissionsOnly="['field.employee_salary']">
                      <div class="flex-1">
                        <nz-input-number
                          [nzPlaceHolder]="'calendar.form.fixatedSalary' | translate"
                          [nzSize]="'large'"
                          formControlName="salaryFixed"
                          [nzDisabled]="isEmployee"
                          class="w-full"
                        ></nz-input-number>
                      </div>

                      <div class="flex-1">
                        <nz-input-number
                          [nzPlaceHolder]="'calendar.form.salary%' | translate"
                          [nzSize]="'large'"
                          formControlName="salaryPercent"
                          [nzMin]="0"
                          [nzMax]="100"
                          [nzDisabled]="isEmployee"
                          class="w-full"
                        ></nz-input-number>
                      </div>
                    </ng-container>

                    <div class="w-[52px] flex justify-center">
                      <button
                        nz-button
                        nzType="default"
                        nzDanger
                        nzSize="large"
                        (click)="removeAdditionalEmployee(idx)"
                        type="button"
                        >
                        <i nz-icon nzType="delete" nzTheme="outline"></i>
                      </button>
                    </div>
                  </div>
                }
              </div>
            </nz-card>
          }
        </ng-container>
      </section>
    </ng-container>

    <!-- ══ Zone: Comment ══ -->
    <section class="flex flex-col gap-2">
      <div class="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-600">
        <span>{{ 'calendar.form.comment' | translate }}</span>
        <span class="flex-1 h-px bg-gray-200"></span>
      </div>
      <beautyapp-textarea
        formControlName="description"
        placeholder="{{'calendar.form.comment' | translate}}"
        [rows]="3"
      ></beautyapp-textarea>
    </section>

    <beautyapp-appointment-products
      [serviceId]="service()?.id"
      [serviceProducts]="serviceProducts()"
      [bookingStatus]="bookingStatus"
      (serviceProductsUpdate)="serviceProductsUpdate($event)"
    ></beautyapp-appointment-products>
    <beautyapp-appointment-spec-services
      [serviceId]="service()?.id"
      [serviceSpecs]="serviceSpecs()"
      [bookingStatus]="bookingStatus"
      (serviceSpecsUpdate)="serviceSpecsUpdate($event)"
    ></beautyapp-appointment-spec-services>

    <!-- Availability Conflicts Warning -->
    @if (hasAvailabilityConflicts()) {
      <nz-alert
        nzType="warning"
        [nzMessage]="'გაფრთხილება: კონფლიქტები აღმოჩენილია'"
        [nzDescription]="conflictsTemplate"
        nzShowIcon
      ></nz-alert>
      <ng-template #conflictsTemplate>
        <ul class="list-disc pl-4">
          @for (conflict of availabilityConflicts(); track conflict.id) {
            <li [class]="conflict.severity === 'hard' ? 'text-red-600' : 'text-orange-600'">
              @switch (conflict.type) {
                @case ('employee') {
                  <strong>თანამშრომელი:</strong> {{ getConflictMessage(conflict) }}
                }
                @case ('room') {
                  <strong>ოთახი:</strong> {{ getConflictMessage(conflict) }}
                }
                @case ('service') {
                  <strong>სერვისი:</strong> {{ getConflictMessage(conflict) }}
                }
                @case ('additional_employee') {
                  <strong>დამატებითი თანამშრომელი:</strong> {{ getConflictMessage(conflict) }}
                }
                @case ('schedule') {
                  <strong>განრიგი:</strong> {{ getConflictMessage(conflict) }}
                }
                @case ('break') {
                  <strong>შესვენება:</strong> {{ getConflictMessage(conflict) }}
                }
              }
              @if (conflict.severity === 'hard') {
                <nz-tag nzColor="red" class="ml-1 text-xs">ბლოკირებული</nz-tag>
              }
            </li>
          }
        </ul>
      </ng-template>
    }

  </form>

}
<div *nzModalFooter class="flex justify-between items-center gap-4">
  @if (!needChooseService()) {
    <div class="flex flex-1 justify-start items-center gap-4" [formGroup]="form">
      <!-- beaut-240: product cost + additional cost + total. Product cost is
           seeded from the service products and stays editable, because a recipe
           states a nominal amount while actual consumption varies. -->
      <div class="flex flex-col gap-3 flex-initial w-[130px]">
        <beautyapp-input
          label="{{ 'booking.productExpense' | translate }}"
          type="float"
          formControlName="expense"
          size="large"
        ></beautyapp-input>
      </div>
      <div class="flex flex-col gap-3 flex-initial w-[150px]">
        <beautyapp-input
          label="{{ 'booking.additionalExpense' | translate }}"
          type="float"
          formControlName="additionalExpense"
          size="large"
        ></beautyapp-input>
      </div>
      <div class="flex flex-col gap-1 flex-initial w-[130px]">
        <div class="text-xs flex items-center h-[24px]">
          {{ 'booking.totalExpense' | translate }}
        </div>
        <div class="flex items-center gap-1 border border-gray-300 rounded-[7px] px-4 min-h-[40px] bg-gray-50">
          {{ totalExpense }} <i class="lari lari-c"></i>
        </div>
      </div>
      <div class="self-end">
        <label
          nz-checkbox
          formControlName="sendNotification"
          style="margin-bottom: 10px"
          [nzDisabled]="visitAddFacade.isCompleted"
        >{{ 'addVisit.form.sendNotification' | translate }}</label>
      </div>
    </div>
  }
  <div class="flex gap-2" style="padding-top: 25px">
    <button nz-button nzType="default" nzSize="large" (click)="modalRef.close()">{{ 'buttons.close' | translate }}
    </button>
    @if (!needChooseService()) {
      @if (hasAvailabilityConflicts()) {
        <button type="button" nz-button nzSize="large" nzType="primary" nzDanger
          [nzLoading]="loading()"
          [disabled]="hasHardConflicts() || loading()"
          (click)="confirmDespiteConflicts()">მაინც შენახვა
        </button>
      } @else {
        <button type="button" nz-button nzSize="large" nzType="primary"
          [nzLoading]="loading() || isCheckingAvailability()"
          [disabled]="loading() || isCheckingAvailability()"
          (click)="submit()">{{ isCheckingAvailability() ? 'შემოწმება...' : ('buttons.save' | translate) }}
        </button>
      }
    }
  </div>
</div>
